import { useEffect } from "react";

export interface SocialMeta {
  type?: "website" | "article";
  description?: string;
  image?: string;
}

const IMAGE_META: [string, string][] = [
  ["property", "og:image"],
  ["property", "og:image:width"],
  ["property", "og:image:height"],
  ["property", "og:image:type"],
  ["name", "twitter:image"],
];

function upsertMeta(attr: "name" | "property", key: string, content: string): void {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attr, key);
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function syncImageMeta(image?: string): void {
  if (!image) {
    IMAGE_META.forEach(([attr, key]) => document.head.querySelector(`meta[${attr}="${key}"]`)?.remove());
    return;
  }
  const values = [image, "1200", "630", "image/png", image];
  IMAGE_META.forEach(([attr, key], i) => upsertMeta(attr as "name" | "property", key, values[i]));
}

export function usePageMeta(title: string, description: string, canonical?: string, social?: SocialMeta): void {
  const ogType = social?.type ?? "website";
  const ogDescription = social?.description ?? description;
  const image = social?.image;
  useEffect(() => {
    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", ogDescription);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:site_name", "Gepeto.life");
    upsertMeta("property", "og:locale", "pt_BR");
    if (canonical) upsertMeta("property", "og:url", canonical);
    upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", ogDescription);
    syncImageMeta(image);
  }, [title, description, canonical, ogType, ogDescription, image]);

  useEffect(() => {
    if (!canonical) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonical;
    const node = link;
    return () => node.remove();
  }, [canonical]);
}
